import React from 'react';
import {
  Box,
  Divider,
  Stack,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import step1 from '../assets/images/account-deletion-step1.png';
import step2 from '../assets/images/account-deletion-step2.png';
import step3 from '../assets/images/account-deletion-step3.png';
import step4 from '../assets/images/account-deletion-step4.png';
import { Link } from 'react-router-dom';

const sectionHeading = {
  fontWeight: 700,
  mt: 2,
  mb: 1,
};

const paragraph = {
  color: 'text.primary',
  lineHeight: 1.4,
};

const listStyle = {
  color: 'text.primary',
  fontSize: '0.975rem',
  lineHeight: 1.8,
  m: 0,
};

const faqs = [
  {
    question: 'What does Genie Transcribe do?',
    answer:
      'Genie Transcribe helps you record audio, convert speech to text, generate AI summaries, and manage transcripts.',
  },
  {
    question: 'Do I need an internet connection?',
    answer:
      'You can make offline recordings without internet connection but other features may require an internet connection, especially real-time transcription, AI summaries, account login, calendar event fetching, and syncing app data.',
  },
  {
    question: 'How do I start a recording?',
    answer:
      'Open the app, go to the recording screen, and tap the record button. Make sure microphone permission is enabled for the app.',
  },
  {
    question: 'Why is my transcription not appearing?',
    answer:
      'Check your internet connection, confirm that the recording completed successfully, and make sure the audio is clear enough for transcription.',
  },
  {
    question: 'How do I improve transcription quality?',
    answer:
      'Record in a quiet environment, keep the speaker close to the microphone, and avoid overlapping voices when possible.',
  },
  {
    question: 'Can I edit or manage my transcripts?',
    answer:
      'Yes. You can view and manage transcripts inside the app. Available options may depend on your app version and account access.',
  },
  {
    question: 'How do I get AI-generated summaries?',
    answer:
      'After a recording is transcribed, use the summary option in the app to generate an AI summary when available.',
  },
  {
    question: 'What should I do if the app is not working correctly?',
    answer:
      'Restart the app, check your internet connection, update to the latest app version, and contact support if the issue continues.',
  },
  {
    question: 'How do I report a bug or share feedback?',
    answer: (
      <span>
        Email{' '}
        <a href="mailto:genie@mavenvista.com">genie@mavenvista.com</a>{' '}
        with your device model, iOS version, app version, and a short description of the issue.
      </span>
    ),
  },
  {
    question: 'How can I delete my account?',
    answer:
      'Open your profile or account page in the app, tap Delete Account below Log Out, and confirm the request. You can also review the detailed account deletion steps linked above.',
  },
];

export default function SupportPage() {
  return (
    <Box sx={{ maxWidth: 1080, width: '90%', mx: 'auto' }}>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
          bgcolor: 'background.paper',
          // borderRadius: 3,
          // boxShadow: '0 20px 60px rgba(15, 23, 42, 0.08)',
          p: { xs: 4, md: 5 },
        }}
      >
        <Stack direction="row" alignItems="center" spacing={2} sx={{ flexWrap: 'wrap' }}>
          {/* <Box
            component="img"
            src={logo}
            alt="VENDX Genie logo"
            sx={{ height: 50, width: 'auto', display: 'block' }}
          /> */}
          <Box>
            <Typography variant="h4" component="h1" color='primary.main' sx={{ fontWeight: 700, }}>
              Transcribe Support
            </Typography>
          </Box>
        </Stack>

        <Typography sx={paragraph}>
          Welcome to Genie Transcribe Support.
        </Typography>

        <Typography sx={paragraph}>
          If you encounter any issues with the app or have suggestions for improvements, please contact us at  <strong>Email:</strong> <a href="mailto:genie@mavenvista.com">genie@mavenvista.com</a>
        </Typography>

        <Divider />

        <Typography variant='h5' sx={sectionHeading}>1. Need Help?</Typography>
        <Typography sx={paragraph}>
          Need Help? contact <a href="mailto:genie@mavenvista.com">genie@mavenvista.com</a>.
        </Typography>
        <Typography sx={paragraph}>
          Please include the following details so we can investigate quickly:
        </Typography>
        <Box component="ul" sx={listStyle}>
          <li>Device model</li>
          <li>iOS version</li>
          <li>App version</li>
          <li>Description of the issue</li>
          <li>Steps to reproduce the issue, if available</li>
        </Box>

        <Typography variant='h5' sx={sectionHeading}>2. Privacy Policy</Typography>
        <Typography sx={paragraph}>
          Read the Genie Transcribe Privacy Policy: <Link to="/privacy" underline="always">Privacy Policy</Link>
        </Typography>

        <Typography variant='h5' sx={sectionHeading}>3. Account Deletion</Typography>
        <Typography sx={paragraph}>
          You can request account deletion from within the Genie Transcribe mobile app.
        </Typography>
        <Typography sx={paragraph}>
          To delete your account:
        </Typography>
        <Box component="ul" sx={listStyle}>
          <li>Open the Genie Transcribe app.</li>
          <li>Go to your profile or account page.</li>
          <li>Tap Delete Account below Log Out.</li>
          <li>Review the confirmation message.</li>
          <li>Confirm the deletion request.</li>
        </Box>
        <Typography sx={paragraph}>
          After your deletion request is accepted, your account will be logged out. User data may be retained for 90 days before permanent deletion for audit purposes.
        </Typography>
        <Typography sx={paragraph}>
          Account Deletion Steps:
        </Typography>
        <Box component="ul" sx={listStyle}>
          <li>Login into your account
            <img src={step1} alt="Step 1: Login into your account" style={{ height: 500, width: 'auto', display: 'block', margin: '12px 0' }} />
          </li>
          <li>Click on your user profile
            <img src={step2} alt="Step 2: Click on your user profile" style={{ height: 250, width: 'auto', display: 'block', margin: '12px 0' }} />
          </li>
          <li>Click on Delete account
            <img src={step3} alt="Step 3: Click on Delete account" style={{ height: 500, width: 'auto', display: 'block', margin: '12px 0' }} />
          </li>
            <li>Confirm Deletion
            <img src={step4} alt="Step 4: Confirm account deletion" style={{ height: 500, width: 'auto', display: 'block', margin: '12px 0' }} />
          </li>
        </Box>

        <Typography variant='h5' sx={sectionHeading}>4. Contact Us</Typography>
        <Typography sx={{ ...paragraph, mb: 0 }}>
          For all support requests, feedback, or account deletion questions:
        </Typography>
        <Typography sx={{ ...paragraph, mb: 0 }}>
          <strong>Email:</strong> <a href="mailto:genie@mavenvista.com">genie@mavenvista.com</a>
        </Typography>
      </Box>
      <Box className="faqs" sx={{ mt: 4, pb: { xs: 4, md: 5 }, }}>
        <Typography variant="h5" sx={sectionHeading}>
          Frequently Asked Questions
        </Typography>
        {faqs.map((faq, index) => (
          <Accordion
            key={index}
            sx={{
              borderRadius: 2,
              mt: index === 0 ? 2 : 1,
              boxShadow: 'none',
              border: '1px solid rgba(0, 0, 0, 0.08)',
            }}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography sx={{ fontWeight: 600 }}>{faq.question}</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Typography sx={paragraph}>{faq.answer}</Typography>
            </AccordionDetails>
          </Accordion>
        ))}
      </Box>
    </Box>
  );
}
